import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { queryKeys } from "@/lib/queryKeys";
import { type User, usersProvider } from "@/providers/users.provider";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function UserPicker({
  selected,
  onSelect,
  placeholder = "Search by username, email or id…",
}: {
  selected: User | null;
  onSelect: (user: User | null) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const { data: results, isFetching } = useQuery({
    queryKey: queryKeys.usersSearch(debouncedQuery),
    queryFn: () => usersProvider.search(debouncedQuery),
    enabled: debouncedQuery.length >= 1,
    staleTime: 30_000,
  });

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-lg border px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{selected.username}</p>
          <p className="truncate text-xs text-muted-foreground font-mono">
            {selected.user_id}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 text-xs"
          onClick={() => {
            onSelect(null);
            setQuery("");
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        placeholder={placeholder}
        value={query}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && query.trim().length >= 1 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-md">
          {isFetching ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
          ) : !results?.length ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No users found
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {results.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                  onClick={() => {
                    onSelect(u);
                    setOpen(false);
                  }}
                >
                  <span className="text-sm">{u.username}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {u.user_id}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
